import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { User } from "firebase/auth";
import { CssBaseline } from "@mui/material";
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { CookieConsentBanner } from './components/cookieConsent';
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ExperimentsProvider } from "./contexts/ExperimentsProvider";
import { Toaster } from "sonner";
import { captureReferralFromURL } from "./utils/referralStorage";

// i18n initialization (side-effect import — must be before any component renders)
import "./i18n";
import { useTranslation } from "react-i18next";
import { useLanguageSync } from "./hooks/useLanguageSync";

// Eager-loaded components (needed immediately)
import ProtectedRoute from "./components/ProtectedRoute";
import PageTracker from "./components/PageTracker";
import AppNavbar from "./components/AppNavbar";
import AppTheme from "./theme/AppTheme";
import FloatingProcessingIndicator from "./components/FloatingProcessingIndicator";
import SplashScreen from "./components/SplashScreen";
import { GuidedTourProvider } from "./components/guidedTour";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy-loaded route components (code splitting optimization)
const ReferralRedirect = lazyWithRetry(() => import("./components/ReferralRedirect"));
const Dashboard = lazyWithRetry(() => import("./Dashboard"));
const MarketingPage = lazyWithRetry(() => import("./MarketingPage"));
const SignIn = lazyWithRetry(() => import("./SignIn"));
const PricingPage = lazyWithRetry(() => import("./marketingComponents/PricingPage"));
const OnboardingV3 = lazyWithRetry(() => import("./components/onboardingV2/OnboardingV3"));
const PluginPrivacyTermsPage = lazyWithRetry(() => import("./components/PluginPrivacyTermsPage"));
const Reprendre = lazyWithRetry(() => import("./pages/Reprendre"));

// Landing page (production)
const LandingProposalC = lazyWithRetry(() => import("./pages/LandingProposalC"));
const BlogPage = lazyWithRetry(() => import("./pages/BlogPage"));
const BlogPostPage = lazyWithRetry(() => import("./pages/BlogPostPage"));
// Dev tools (only for development)
const TemplateScreenshotGenerator = lazyWithRetry(() => import("./pages/TemplateScreenshotGenerator"));
const DevStep7V4Sandbox = lazyWithRetry(() => import("./pages/DevStep7V4Sandbox"));
const DevScoreSplitDemo = lazyWithRetry(() => import("./pages/DevScoreSplitDemo"));
const ScoreFinalize = lazyWithRetry(() => import("./pages/ScoreFinalize"));
const ScorePage = lazyWithRetry(() => import("./pages/ScorePage"));
const AdaptPageSwitch = lazyWithRetry(() => import("./pages/adapt/AdaptPageSwitch"));
const LettreMotivationPage = lazyWithRetry(() => import("./pages/LettreMotivationPage"));
const AdaptedCVPage = lazyWithRetry(() => import("./pages/AdaptedCVPage"));
const OutilsHubPage = lazyWithRetry(() => import("./pages/OutilsHubPage"));
const AnalyseurATSPage = lazyWithRetry(() => import("./pages/AnalyseurATSPage"));
const ModelesCVPage = lazyWithRetry(() => import("./pages/ModelesCVPage"));
const GuideCVSuissePage = lazyWithRetry(() => import("./pages/GuideCVSuissePage"));
const PartenairesOrpPage = lazyWithRetry(() => import("./pages/PartenairesOrpPage"));
const MethodologiePage = lazyWithRetry(() => import("./pages/MethodologiePage"));
const UnsubscribePage = lazyWithRetry(() => import("./pages/UnsubscribePage"));
const ChurnSurveyThankYou = lazyWithRetry(() => import("./pages/ChurnSurveyThankYou"));
const PrivacyPage = lazyWithRetry(() => import("./pages/PrivacyPage"));
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage"));
const LegalPage = lazyWithRetry(() => import("./pages/LegalPage"));
const SubmitTestimonial = lazyWithRetry(() => import("./pages/SubmitTestimonial"));
const JulienCardPage = lazyWithRetry(() => import("./pages/JulienCardPage"));
const NotFoundPage = lazyWithRetry(() => import("./pages/NotFoundPage"));

function App() {
    return (
        <ErrorBoundary level="page">
            <CookieConsentProvider>
                <AuthProvider>
                    <ExperimentsProvider>
                    <SubscriptionProvider>
                        <Suspense fallback={null}>
                            <AppTheme>
                                <CssBaseline enableColorScheme />
                                <GuidedTourProvider>
                                    <Router>
                                        <PageTracker />
                                        <Toaster
                                            position="top-right"
                                            richColors
                                            closeButton
                                            expand={false}
                                            duration={5000}
                                        />
                                        <AppContentWrapper />
                                        <CookieConsentBanner />
                                    </Router>
                                </GuidedTourProvider>
                            </AppTheme>
                        </Suspense>
                    </SubscriptionProvider>
                    </ExperimentsProvider>
                </AuthProvider>
            </CookieConsentProvider>
        </ErrorBoundary>
    );
}

function AppContentWrapper() {
    const { user, loading } = useAuth();
    const [showSplash, setShowSplash] = useState(true);
    // Keeps the splash in the DOM during its CSS fade-out, then unmounts it.
    const [splashMounted, setSplashMounted] = useState(true);

    // Sync i18next language with user's Firestore preference
    useLanguageSync();

    // Dynamic lang attribute update when i18n language changes
    const { i18n } = useTranslation();
    useEffect(() => {
        document.documentElement.lang = i18n.language === 'de-CH' ? 'de' : i18n.language;
    }, [i18n.language]);

    // Capture referral params (?ref=...&source=...) from the URL into sessionStorage.
    // Runs once on mount — covers all routes (landing, login, etc.)
    useEffect(() => {
        if (window.location.search.includes('ref=')) {
            captureReferralFromURL();
        }
    }, []);

    // Google Analytics is now initialized by CookieConsentContext when consent is given

    // Delayed splash removal for smooth transition
    useEffect(() => {
        if (!loading) {
            // Small delay to ensure content is ready before hiding splash
            const timer = setTimeout(() => {
                setShowSplash(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [loading]);

    // Once the splash starts fading out, unmount it after the CSS transition
    // (0.5s) completes — mirrors framer-motion's AnimatePresence exit.
    useEffect(() => {
        if (!showSplash) {
            const timer = setTimeout(() => {
                setSplashMounted(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [showSplash]);

    return (
        <>
            {/* Main content - render behind splash (CSS fade-in, 0.4s ease-out) */}
            {!loading && (
                <div
                    style={{
                        minHeight: '100vh',
                        opacity: 0,
                        animation: 'appContentFadeIn 0.4s ease-out forwards',
                    }}
                >
                    <style>{`@keyframes appContentFadeIn { to { opacity: 1; } }`}</style>
                    <AppContent user={user} />
                </div>
            )}

            {/* Splash screen with CSS fade-out exit (0.5s ease-in-out) */}
            {splashMounted && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        opacity: showSplash ? 1 : 0,
                        transition: 'opacity 0.5s ease-in-out',
                        pointerEvents: showSplash ? 'auto' : 'none',
                    }}
                >
                    <SplashScreen />
                </div>
            )}
        </>
    );
}

/**
 * Lightweight route-level fallback — invisible for fast loads,
 * shows a minimal spinner only after 300ms delay.
 * Prevents the heavy SplashScreen from flashing during in-app navigation.
 */
function RouteLoadingFallback() {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setVisible(true), 300);
        return () => clearTimeout(t);
    }, []);
    if (!visible) return <div style={{ minHeight: '100vh' }} />;
    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            animation: 'routeFadeIn 0.3s ease 0s forwards',
        }}>
            <style>{`@keyframes routeFadeIn { to { opacity: 0.5; } }`}</style>
            <div style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '3px solid #DCFCE7',
                borderTopColor: '#22C55E',
                animation: 'routeSpin 0.8s linear infinite',
            }} />
            <style>{`@keyframes routeSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

// Composant interne pour avoir accès à useLocation
function AppContent({ user }: { user: User | null }) {
    const location = useLocation();
    const isAuthenticatedRoute = location.pathname.startsWith('/dashboard');

    return (
        <>
            {/* AppNavbar visible uniquement sur les routes authentifiées en mobile */}
            {user && isAuthenticatedRoute && <AppNavbar />}
            {user && <FloatingProcessingIndicator />}
            <ErrorBoundary level="page">
            <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                    <Route path="/login" element={<SignIn />} />
                    <Route path="/reprendre" element={<Reprendre />} />
                    <Route path="/" element={<LandingProposalC />} />
                    <Route path="/landing" element={<LandingProposalC />} />
                    <Route path="/landing-old" element={<MarketingPage />} />
                    <Route path="/landing-c" element={<LandingProposalC />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/de/blog" element={<BlogPage lang="de" />} />
                    <Route path="/de/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/r/:code" element={<ReferralRedirect />} />
                    <Route path="/score" element={<ScorePage />} />
                    <Route path="/demo-adaptation" element={<AdaptPageSwitch />} />
                    <Route path="/lettre-motivation" element={<LettreMotivationPage />} />
                    <Route path="/mon-cv-adapte" element={<AdaptedCVPage />} />
                    <Route path="/outils" element={<OutilsHubPage />} />
                    <Route path="/outils/analyseur-ats" element={<AnalyseurATSPage />} />
                    <Route path="/outils/modeles-cv" element={<ModelesCVPage />} />
                    <Route path="/guide/cv-suisse" element={<GuideCVSuissePage />} />
                    <Route path="/partenaires-orp" element={<PartenairesOrpPage />} />
                    <Route path="/methodologie" element={<MethodologiePage />} />
                    <Route path="/testimonial/submit" element={<SubmitTestimonial />} />
                    <Route path="/julien" element={<JulienCardPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/legal" element={<LegalPage />} />
                    <Route path="/plugin-privacy-terms" element={<PluginPrivacyTermsPage />} />
                    <Route path="/unsubscribe" element={<UnsubscribePage />} />
                    <Route path="/enquete/merci" element={<ChurnSurveyThankYou />} />
                    <Route path="/signin" element={<SignIn />} />
                    {/* Dev tools */}
                    <Route path="/dev/template-screenshots" element={<TemplateScreenshotGenerator />} />
                    <Route path="/dev/step7-v4" element={<DevStep7V4Sandbox />} />
                    <Route path="/dev/score-split-demo" element={<DevScoreSplitDemo />} />
                    {/* Bypass d'onboarding pour les leads venus du funnel /score :
                        leur CV est déjà analysé, on le finalise et on les envoie
                        droit sur leur CV. Sert le bundle app (aucun rewrite
                        marketing ne matche /score/finalize, cf. firebase.json). */}
                    <Route
                        path="/score/finalize"
                        element={
                            !user ? (
                                <Navigate to="/landing" />
                            ) : (
                                <ScoreFinalize />
                            )
                        }
                    />
                    <Route
                        path="/onboarding"
                        element={
                            !user ? (
                                <Navigate to="/landing" />
                            ) : (
                                <OnboardingV3 />
                            )
                        }
                    />
                    <Route
                        path="/dashboard/*"
                        element={
                            !user ? (
                                <Navigate to="/landing" />
                            ) : (
                                <Dashboard />
                            )
                        }
                    />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </Suspense>
            </ErrorBoundary>
        </>
    );
}

export default App;
